/**
 * Two-column layout: image on one side, content slot on the other.
 * Image alternates left/right via `isImageRight`. On mobile, content stacks above image.
 * Use as the base for any image + text page section.
 */
import { cn } from '@lib/util/cn';
import Image from 'next/image';

type EditorialSectionImage = {
  src: string;
  alt: string;
};

type EditorialSectionProps = {
  image: EditorialSectionImage;
  headingId: string;
  isImageRight?: boolean;
  isPriority?: boolean;
  className?: string;

  children: React.ReactNode;
};

export default function EditorialSection({
  image,
  headingId,
  isImageRight = false,
  isPriority = false,
  className = 'bg-[#f1f4f0]',
  children,
}: EditorialSectionProps) {
  return (
    <section className={className} aria-labelledby={headingId}>
      <div className="content-container py-11">
        <div className="grid grid-cols-1 gap-11 desktop:grid-cols-2 desktop:gap-10 desktop:items-center">
          <div
            className={cn(
              'relative order-last rounded-lg overflow-hidden h-[235px] tablet:h-[355px] desktop:h-[510px]',
              isImageRight ? 'desktop:order-2' : 'desktop:order-1',
            )}
          >
            <Image
              src={image.src}
              alt={image.alt}
              fill
              className="object-cover"
              sizes="(min-width: 1100px) 50vw, 100vw"
              fetchPriority={isPriority ? 'high' : undefined}
            />
          </div>

          <div
            className={cn(
              'flex flex-col gap-11 desktop:gap-8 order-first',
              isImageRight ? 'desktop:order-1' : 'desktop:order-2',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
